/**
 * Verifies a Waldogram configuration object.
 *
 * @param {Object} config - The Waldogram configuration to verify.
 * @returns {Array} - Any possible errors found or an empty array.
 */
export const verifyWaldogramConfig = config => {
  const errors = [];
  const { photos } = config;

  // Count valid photos - either containing a waldo photo object or an uploaded assed Id.
  const validPhotos = photos.filter(
    photo => (!!photo.photo && !!photo.photo.id) || !!photo.uploadedAssetId,
  );

  if (!validPhotos.find(p => p.position === 0)) {
    errors.push('Please select the main photo for your card.');
  }

  if (!config.identityName) {
    errors.push('Missing recipient full name.');
  }

  if (config.identityName && !config.identityName.trim().includes(' ')) {
    errors.push('Please make sure you’ve added a last name.');
  }

  if (!config.caption) {
    errors.push('Please type a message!');
  }

  return errors;
};

/**
 * Attempts to calculate and return the remaining characters allowed
 * for a Waldogram's caption.
 *
 * @param {WALDOGRAM_TEXT_CONFIGS} config - The text configuration (rows / chars per row).
 * @param {string} caption - The caption.
 * @returns {{total: number, max: number, remaining: number}}
 */
export const getWaldogramRemainingCaptionChars = (config, caption) => {
  if (!config) {
    return {
      max: 0,
      total: 0,
      remaining: 0,
    };
  }

  const { rows, charsPerRow } = config;
  const maxAllowedChars = rows * charsPerRow;

  // Split the text by newlines.
  const lines = caption.split(/\r?\n/);

  // For each newline calculate how many effective chars we should remove from
  // the maximum total allowance.
  const totalChars = lines.reduce((max, line, index) => {
    // For the very last line we want to accurately count the exact chars
    // of that line.
    if (index === lines.length - 1) {
      return max + line.length;
    }

    // For every other line we either count the char count or the charsPerRow,
    // whichever is bigger.
    return max + Math.max(config.charsPerRow, line.length);
  }, 0);

  return {
    max: maxAllowedChars,
    total: totalChars,
    remaining: maxAllowedChars - totalChars,
  };
};

/**
 * Sanitizes the unified emoji string returned by the emoji picker
 * so that it removes the invisible variation selection codepoint for usage
 * of the string with an emoji CDN.
 *
 * @param {string} codepoint - The codepoint as returned from the emoji picker.
 * @returns {string}
 */
export const sanitizeEmojiPickerCodepointForDisplay = codepoint => {
  const unicodeArray = codepoint.split('-');

  if (unicodeArray.length > 2) {
    return codepoint;
  }

  // Remove the U+FE0F invisible codepoint as it is only used in plain text
  // https://emojipedia.org/variation-selector-16/
  return codepoint.replace('-fe0f', '');
};

/**
 * Given the `config` object generated by useWaldogramConfig {@see useWaldogramConfig}
 * it generates the `photos` object that CustomPrintConfigurationInput expects.
 *
 * @param {Object} config The config as returned by useWaldogramConfig.
 * @param {Array} config.photos The photos selected by the user.
 * @param {string} [config.emoji] The emoji selected by the user (hardcoded for use in the second photo).
 * @returns {*}
 */
export const mapWaldogramConfigToPhotosInput = config => {
  const validPhotos = config.photos.filter(
    photo => (!!photo.photo && !!photo.photo.id) || !!photo.uploadedAssetId,
  );

  return validPhotos.map(({ position, photo, cropbox }) => {
    return {
      ...(photo.custom ? { asset_image_id: photo.id } : { photo_id: photo.id }),
      position,
      emoji:
        position === 1 &&
        config.emoji &&
        config.emoji.unified.split('-').join(' '),
      cropbox,
    };
  });
};
